import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { fetchFoodInvoices, type FoodInvoice } from "../foodApi";
import { money, shortDate } from "../utils/foodFormatters";
import { QueryError } from "../components/QueryError";
import { SimpleTable } from "../components/SimpleTable";

const COLUMNS: { key: string; title: string; render: (r: FoodInvoice) => ReactNode }[] = [
  {
    key: "vendor",
    title: "Vendor",
    render: (r) => (
      <Link to={`/food/invoices/${r.id}`} className="text-tribal-red hover:underline">
        {r.vendorName}
      </Link>
    ),
  },
  { key: "invoiceNumber", title: "Invoice #", render: (r) => r.invoiceNumber ?? "-" },
  { key: "invoiceDate", title: "Date", render: (r) => shortDate(r.invoiceDate) },
  { key: "totalAmount", title: "Total", render: (r) => money(r.totalAmount) },
  { key: "lineItemCount", title: "Lines", render: (r) => r.lineItemCount },
];

export function InvoicesView() {
  const q = useQuery({ queryKey: ["food", "invoices"], queryFn: fetchFoodInvoices });

  if (q.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-20 text-sm text-tribal-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading invoices...
      </div>
    );
  }
  if (q.isError || !q.data) {
    return <QueryError message={`Failed to load invoices: ${(q.error as Error).message}`} />;
  }

  return (
    <div className="p-4 sm:p-6">
      <section className="rounded-lg border border-tribal-border bg-tribal-surface">
        <SimpleTable<FoodInvoice> rows={q.data} empty="No invoices found." columns={COLUMNS} />
      </section>
    </div>
  );
}
